import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { AppLayout, RequirePermission } from './layout';
import { ApiError } from './lib/api';
import { AuthProvider } from './lib/auth';
import { registerServiceWorker } from './lib/push';
import { ToastProvider } from './lib/ui';
import { AuditPage, BroadcastPage, EmailLogPage, SettingsPage } from './pages/admin';
import { ForgotPasswordPage, LoginPage, ResetPasswordPage } from './pages/auth';
import { HomePage, NotificationsPage, ProfilePage } from './pages/home';
import { DepartmentsPage, UsersPage } from './pages/users';
import { RoutesPage } from './pages/routes';
import { GroupsPage } from './pages/groups';
import { RegulationsPage } from './pages/regulations';
import { GuidesPage, GuideViewPage } from './pages/guides';
import { RequestTypesPage } from './pages/request-types';
import { TemplatesPage } from './pages/templates';
import { ReportsPage } from './pages/reports';
import { NewsPage } from './pages/news';
import { FileViewPage } from './components/files';
import { SubstitutionsPage } from './pages/substitutions';
import { TaskPage } from './pages/task';
import { NewTaskPage, TasksPage } from './pages/tasks';
import './styles.css';

registerServiceWorker();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      refetchOnWindowFocus: true,
    },
  },
});

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  {
    element: <AppLayout />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'inbox', element: <TasksPage inbox /> },
      { path: 'tasks', element: <TasksPage /> },
      { path: 'trips', element: <TasksPage requestType="trip" /> },
      { path: 'tasks/new', element: <NewTaskPage /> },
      { path: 'regulations', element: <RegulationsPage /> },
      { path: 'guides', element: <GuidesPage /> },
      { path: 'guides/:key', element: <GuideViewPage /> },
      { path: 'tasks/:id', element: <TaskPage /> },
      { path: 'tasks/:id/files/:fileId', element: <FileViewPage /> },
      { path: 'routes', element: <RequirePermission permission="routes.manage"><RoutesPage /></RequirePermission> },
      { path: 'approver-groups', element: <RequirePermission permission="routes.manage"><GroupsPage /></RequirePermission> },
      { path: 'request-types', element: <RequirePermission permission="request_types.manage"><RequestTypesPage /></RequirePermission> },
      { path: 'templates', element: <RequirePermission permission="templates.manage"><TemplatesPage /></RequirePermission> },
      { path: 'settings', element: <RequirePermission permission="settings.manage"><SettingsPage /></RequirePermission> },
      { path: 'profile', element: <ProfilePage /> },
      { path: 'news', element: <NewsPage /> },
      { path: 'reports', element: <RequirePermission permission="reports.view"><ReportsPage /></RequirePermission> },
      { path: 'substitutions', element: <RequirePermission permission="users.manage"><SubstitutionsPage /></RequirePermission> },
      { path: 'notifications', element: <NotificationsPage /> },
      { path: 'users', element: <RequirePermission permission="users.manage"><UsersPage /></RequirePermission> },
      { path: 'departments', element: <RequirePermission permission="users.manage"><DepartmentsPage /></RequirePermission> },
      { path: 'broadcast', element: <RequirePermission permission="notifications.broadcast"><BroadcastPage /></RequirePermission> },
      { path: 'audit', element: <RequirePermission permission="audit.read"><AuditPage /></RequirePermission> },
      { path: 'email-log', element: <RequirePermission permission="email_log.read"><EmailLogPage /></RequirePermission> },
      { path: '*', element: <div className="empty"><span className="emoji">🧭</span><b>Страница не найдена</b></div> },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
